import React, { useState, useRef, useEffect } from 'react';
import { Search, Globe, Check, X, FileText, Loader2, Plane, ChevronDown, Send } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get country flag image URL
const getFlag = (code) => `https://flagcdn.com/w40/${code.toLowerCase()}.png`;

// Custom dropdown with flags
const FlagSelect = ({ value, onChange, placeholder, countries, testId, isLight: isLightProp }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);
  const isLight = isLightProp;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const selected = countries.find(c => c.code === value);
  const filtered = countries.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} style={{ flex: '1 1 120px', minWidth: '0', position: 'relative', zIndex: 100 }} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '10px 12px', background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.07)',
          border: isLight ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
          color: isLight ? '#1a1a2e' : '#fff', fontSize: '13px', cursor: 'pointer', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: '8px', transition: 'border-color 0.2s',
          borderColor: open ? '#5EFCE8' : (isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)')
        }}
        data-testid={`${testId}-trigger`}
      >
        {selected ? (
          <>
            <img src={getFlag(selected.code)} alt="" style={{ width: '22px', height: '15px', borderRadius: '2px', objectFit: 'cover', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{selected.name}</span>
          </>
        ) : (
          <span style={{ color: isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)', flex: 1 }}>{placeholder}</span>
        )}
        <ChevronDown size={14} style={{ color: isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)', flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
          background: isLight ? '#ffffff' : '#141829', border: isLight ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.12)',
          borderRadius: '10px', zIndex: 9999, overflow: 'hidden',
          boxShadow: isLight ? '0 12px 40px rgba(0,0,0,0.15)' : '0 12px 40px rgba(0,0,0,0.6)'
        }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <input
              ref={inputRef}
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              data-testid={`${testId}-search`}
              style={{
                width: '100%', padding: '8px 10px', background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)', borderRadius: '6px',
                color: isLight ? '#1a1a2e' : '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '12px', color: isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)', fontSize: '13px', textAlign: 'center' }}>No results</div>
            )}
            {filtered.map(c => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange(c.code); setOpen(false); setSearch(''); }}
                data-testid={`${testId}-option-${c.code}`}
                style={{
                  width: '100%', padding: '8px 10px', background: value === c.code ? 'rgba(0,212,170,0.12)' : 'transparent',
                  border: 'none', borderRadius: '6px', color: isLight ? '#1a1a2e' : '#fff', fontSize: '13px',
                  cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => { if (value !== c.code) e.currentTarget.style.background = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={(e) => { if (value !== c.code) e.currentTarget.style.background = 'transparent'; }}
              >
                <img src={getFlag(c.code)} alt="" style={{ width: '22px', height: '15px', borderRadius: '2px', objectFit: 'cover', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                {value === c.code && <Check size={14} style={{ color: '#5EFCE8', marginLeft: 'auto', flexShrink: 0 }} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const COUNTRIES = [
  { code: "AF", name: "Afghanistan" }, { code: "AL", name: "Albania" }, { code: "DZ", name: "Algeria" },
  { code: "AD", name: "Andorra" }, { code: "AO", name: "Angola" }, { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" }, { code: "AM", name: "Armenia" }, { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" }, { code: "AZ", name: "Azerbaijan" }, { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" }, { code: "BD", name: "Bangladesh" }, { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" }, { code: "BE", name: "Belgium" }, { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" }, { code: "BT", name: "Bhutan" }, { code: "BO", name: "Bolivia" },
  { code: "BA", name: "Bosnia and Herzegovina" }, { code: "BW", name: "Botswana" }, { code: "BR", name: "Brazil" },
  { code: "BN", name: "Brunei" }, { code: "BG", name: "Bulgaria" }, { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" }, { code: "CV", name: "Cabo Verde" }, { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" }, { code: "CA", name: "Canada" }, { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" }, { code: "CL", name: "Chile" }, { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" }, { code: "KM", name: "Comoros" }, { code: "CG", name: "Congo" },
  { code: "CR", name: "Costa Rica" }, { code: "CI", name: "Cote d'Ivoire" }, { code: "HR", name: "Croatia" },
  { code: "CU", name: "Cuba" }, { code: "CY", name: "Cyprus" }, { code: "CZ", name: "Czech Republic" },
  { code: "DK", name: "Denmark" }, { code: "DJ", name: "Djibouti" }, { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" }, { code: "EC", name: "Ecuador" }, { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" }, { code: "GQ", name: "Equatorial Guinea" }, { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" }, { code: "SZ", name: "Eswatini" }, { code: "ET", name: "Ethiopia" },
  { code: "FJ", name: "Fiji" }, { code: "FI", name: "Finland" }, { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" }, { code: "GM", name: "Gambia" }, { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" }, { code: "GH", name: "Ghana" }, { code: "GR", name: "Greece" },
  { code: "GD", name: "Grenada" }, { code: "GT", name: "Guatemala" }, { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" }, { code: "GY", name: "Guyana" }, { code: "HT", name: "Haiti" },
  { code: "HN", name: "Honduras" }, { code: "HU", name: "Hungary" }, { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" }, { code: "ID", name: "Indonesia" }, { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" }, { code: "IE", name: "Ireland" }, { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" }, { code: "JM", name: "Jamaica" }, { code: "JP", name: "Japan" },
  { code: "JO", name: "Jordan" }, { code: "KZ", name: "Kazakhstan" }, { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" }, { code: "KP", name: "North Korea" }, { code: "KR", name: "South Korea" },
  { code: "KW", name: "Kuwait" }, { code: "KG", name: "Kyrgyzstan" }, { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" }, { code: "LB", name: "Lebanon" }, { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" }, { code: "LY", name: "Libya" }, { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" }, { code: "LU", name: "Luxembourg" }, { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" }, { code: "MY", name: "Malaysia" }, { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" }, { code: "MT", name: "Malta" }, { code: "MH", name: "Marshall Islands" },
  { code: "MR", name: "Mauritania" }, { code: "MU", name: "Mauritius" }, { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia" }, { code: "MD", name: "Moldova" }, { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" }, { code: "ME", name: "Montenegro" }, { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" }, { code: "MM", name: "Myanmar" }, { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" }, { code: "NP", name: "Nepal" }, { code: "NL", name: "Netherlands" },
  { code: "NZ", name: "New Zealand" }, { code: "NI", name: "Nicaragua" }, { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" }, { code: "MK", name: "North Macedonia" }, { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" }, { code: "PK", name: "Pakistan" }, { code: "PW", name: "Palau" },
  { code: "PA", name: "Panama" }, { code: "PG", name: "Papua New Guinea" }, { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" }, { code: "PH", name: "Philippines" }, { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" }, { code: "QA", name: "Qatar" }, { code: "RO", name: "Romania" },
  { code: "RU", name: "Russia" }, { code: "RW", name: "Rwanda" }, { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" }, { code: "VC", name: "Saint Vincent" }, { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" }, { code: "ST", name: "Sao Tome and Principe" }, { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" }, { code: "RS", name: "Serbia" }, { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" }, { code: "SG", name: "Singapore" }, { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" }, { code: "SB", name: "Solomon Islands" }, { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" }, { code: "SS", name: "South Sudan" }, { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" }, { code: "SD", name: "Sudan" }, { code: "SR", name: "Suriname" },
  { code: "SE", name: "Sweden" }, { code: "CH", name: "Switzerland" }, { code: "SY", name: "Syria" },
  { code: "TW", name: "Taiwan" }, { code: "TJ", name: "Tajikistan" }, { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" }, { code: "TL", name: "Timor-Leste" }, { code: "TG", name: "Togo" },
  { code: "TO", name: "Tonga" }, { code: "TT", name: "Trinidad and Tobago" }, { code: "TN", name: "Tunisia" },
  { code: "TR", name: "Turkey" }, { code: "TM", name: "Turkmenistan" }, { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" }, { code: "UA", name: "Ukraine" }, { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" }, { code: "US", name: "United States" }, { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" }, { code: "VU", name: "Vanuatu" }, { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Vietnam" }, { code: "YE", name: "Yemen" }, { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" }
];

const RESULT_CONFIG = {
  VF: { labelKey: 'vrLabel_VF', descKey: 'vrDesc_VF', color: '#5EFCE8', bg: 'rgba(94, 252, 232, 0.12)', border: 'rgba(94, 252, 232, 0.3)', icon: Check },
  VOA: { labelKey: 'vrLabel_VOA', descKey: 'vrDesc_VOA', color: '#36D399', bg: 'rgba(54, 211, 153, 0.1)', border: 'rgba(54, 211, 153, 0.3)', icon: Plane },
  ETA: { labelKey: 'vrLabel_ETA', descKey: 'vrDesc_ETA', color: '#60A5FA', bg: 'rgba(96, 165, 250, 0.1)', border: 'rgba(96, 165, 250, 0.3)', icon: FileText },
  EV: { labelKey: 'vrLabel_EV', descKey: 'vrDesc_EV', color: '#FFB800', bg: 'rgba(255, 184, 0, 0.1)', border: 'rgba(255, 184, 0, 0.3)', icon: FileText },
  VR: { labelKey: 'vrLabel_VR', descKey: 'vrDesc_VR', color: '#FF4D6A', bg: 'rgba(255, 77, 106, 0.1)', border: 'rgba(255, 77, 106, 0.3)', icon: X },
  NA: { labelKey: 'vrLabel_NA', descKey: 'vrDesc_NA', color: '#888', bg: 'rgba(136, 136, 136, 0.1)', border: 'rgba(136, 136, 136, 0.3)', icon: X },
  SAME: { labelKey: 'vrLabel_SAME', descKey: 'vrDesc_SAME', color: '#888', bg: 'rgba(136, 136, 136, 0.1)', border: 'rgba(136, 136, 136, 0.3)', icon: Check }
};

const VisaChecker = ({ compact = false }) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [passport, setPassport] = useState('');
  const [destination, setDestination] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({ name: '', email: '', mobile: '', uaeResident: '' });
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);

  const canApply = result && (result.code === 'EV' || result.code === 'VOA' || result.code === 'ETA');

  const handleApplySubmit = async () => {
    if (!applyForm.name || !applyForm.mobile || !applyForm.uaeResident) return;
    setApplySubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/visa-apply`, {
        name: applyForm.name,
        email: applyForm.email,
        mobile: applyForm.mobile,
        uae_resident: applyForm.uaeResident,
        passport_country: result?.passport || '',
        destination_country: result?.destination || '',
        visa_type: result?.category || ''
      });
      setApplySuccess(true);
    } catch {
      setApplySuccess(true);
    } finally {
      setApplySubmitting(false);
    }
  };

  const closeApplyModal = () => {
    setShowApplyModal(false);
    setApplyForm({ name: '', email: '', mobile: '', uaeResident: '' });
    setApplySuccess(false);
  };

  const handleCheck = async () => {
    if (!passport || !destination) return;
    if (passport === destination) {
      setError(t('sameCountryError') || 'Citizenship and destination cannot be the same');
      setResult(null);
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/visa-check/${passport}/${destination}`);
      setResult(res.data);
    } catch {
      setError(t('visaCheckError') || 'Could not check visa requirements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cfg = result ? (RESULT_CONFIG[result.code] || RESULT_CONFIG.NA) : null;
  const ResultIcon = cfg?.icon;

  if (compact) {
    return (
      <div data-testid="visa-checker-section" style={{ 
        marginTop: '32px', 
        marginBottom: '28px', 
        width: '100%',
        maxWidth: '420px',
        padding: '0 16px', 
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 100
      }}>
        {/* Glass card container */}
        <div style={{
          padding: '24px 20px',
          background: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(10, 22, 40, 0.85)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '20px',
          border: isLight ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(94, 252, 232, 0.2)',
          boxShadow: isLight ? '0 8px 32px rgba(0, 0, 0, 0.08)' : '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
          overflow: 'visible'
        }}>
          {/* Title with icon */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '20px'
          }}>
            <Globe size={22} style={{ color: '#5EFCE8' }} />
            <h3 style={{ 
              color: isLight ? '#1a1a2e' : '#fff', 
              fontSize: '18px', 
              fontWeight: '700',
              margin: 0,
              letterSpacing: '0.5px'
            }}>
              {t('visaChecker') || 'Visa Checker'}
            </h3>
          </div>

          {/* Dropdowns in a clean grid */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '16px'
          }}>
            {/* Left: Your Citizenship */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('yourCitizenship') || 'Your Citizenship'}
              </label>
              <FlagSelect
                value={passport}
                onChange={(val) => { setPassport(val); setResult(null); setError(''); }}
                placeholder={t('select') || 'Select'}
                countries={COUNTRIES}
                testId="visa-checker-passport"
                isLight={isLight}
              />
            </div>

            {/* Right: Destination */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('destination') || 'Destination'}
              </label>
              <FlagSelect
                value={destination}
                onChange={(val) => { setDestination(val); setResult(null); setError(''); }}
                placeholder={t('select') || 'Select'}
                countries={COUNTRIES}
                testId="visa-checker-destination"
                isLight={isLight}
              />
            </div>
          </div>

          {/* Check Button */}
          <button
            onClick={handleCheck}
            disabled={!passport || !destination || loading}
            data-testid="visa-checker-btn"
            style={{
              width: '100%',
              padding: '14px 20px',
              background: (!passport || !destination) 
                ? 'rgba(0,212,170,0.15)' 
                : 'linear-gradient(135deg, #5EFCE8 0%, #00c49a 100%)',
              border: 'none',
              borderRadius: '12px',
              color: (!passport || !destination) ? (isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.35)') : '#0a1628',
              fontSize: '14px',
              fontWeight: '700',
              cursor: (!passport || !destination) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              boxShadow: (!passport || !destination) ? 'none' : '0 4px 15px rgba(94, 252, 232, 0.3)',
              transform: 'translateY(0)'
            }}
            onMouseEnter={(e) => {
              if (passport && destination && !loading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(94, 252, 232, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = passport && destination ? '0 4px 15px rgba(94, 252, 232, 0.3)' : 'none';
            }}
          >
            {loading ? (
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <>
                <Search size={18} />
                {t('checkVisa') || 'Check Requirements'}
              </>
            )}
          </button>

          {/* Error message */}
          {error && (
            <div style={{ 
              marginTop: '14px', 
              padding: '12px 14px', 
              background: 'rgba(255, 77, 106, 0.1)', 
              border: '1px solid rgba(255, 77, 106, 0.25)', 
              borderRadius: '10px', 
              color: '#FF4D6A', 
              fontSize: '13px', 
              textAlign: 'center' 
            }} data-testid="visa-checker-error">
              {error}
            </div>
          )}

          {/* Result card */}
          {result && cfg && (
            <div
              data-testid="visa-checker-result"
              style={{
                marginTop: '16px',
                padding: '16px',
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                borderRadius: '12px'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '6px'
              }}>
                <ResultIcon size={24} style={{ color: cfg.color }} />
                <span style={{ 
                  fontSize: '16px', 
                  fontWeight: '700', 
                  color: cfg.color 
                }} data-testid="visa-result-status">
                  {t(cfg.labelKey) || cfg.labelKey}
                </span>
              </div>

              {/* Description */}
              <div style={{
                textAlign: 'center',
                color: isLight ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.6)',
                fontSize: '12px',
                lineHeight: '1.5',
                marginBottom: '10px'
              }}>
                {t(cfg.descKey) || cfg.descKey}
              </div>
              
              {/* Country route */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                fontSize: '13px'
              }}>
                {passport && (
                  <img src={getFlag(passport)} alt="" style={{ 
                    width: '24px', height: '16px', borderRadius: '3px', objectFit: 'cover',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                )}
                <span>{result.passport}</span>
                <span style={{ color: cfg.color }}>→</span>
                <span>{result.destination}</span>
                {destination && (
                  <img src={getFlag(destination)} alt="" style={{ 
                    width: '24px', height: '16px', borderRadius: '3px', objectFit: 'cover',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                )}
              </div>
              
              {result.duration && (
                <div style={{ 
                  marginTop: '8px',
                  textAlign: 'center',
                  color: isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)', 
                  fontSize: '12px' 
                }}>
                  {result.duration} {t('days') || 'days'}
                </div>
              )}

              {/* Apply Now button for eVisa/VOA/ETA */}
              {canApply && (
                <button
                  onClick={() => setShowApplyModal(true)}
                  data-testid="visa-apply-btn"
                  style={{
                    width: '100%',
                    marginTop: '16px',
                    padding: '11px',
                    background: 'linear-gradient(135deg, #5EFCE8 0%, #00c49a 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#0a1628',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 14px rgba(94, 252, 232, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Send size={14} />
                  {t('applyNow') || 'Apply Now'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Apply Modal */}
        {showApplyModal && (
          <div
            data-testid="visa-apply-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) closeApplyModal(); }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9999, padding: '20px'
            }}
          >
            <div
              data-testid="visa-apply-modal"
              style={{
                background: isLight ? '#fff' : '#1a2235',
                borderRadius: '20px',
                padding: '28px 24px',
                width: '100%',
                maxWidth: '400px',
                border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                position: 'relative'
              }}
            >
              {/* Close */}
              <button
                onClick={closeApplyModal}
                data-testid="visa-apply-modal-close"
                style={{
                  position: 'absolute', top: '14px', right: '14px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: isLight ? '#666' : '#888', padding: '4px'
                }}
              >
                <X size={20} />
              </button>

              {applySuccess ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{
                    width: '56px', height: '56px', borderRadius: '50%',
                    background: 'rgba(94, 252, 232, 0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Check size={28} style={{ color: '#5EFCE8' }} />
                  </div>
                  <h3 style={{ color: isLight ? '#1a1a2e' : '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 8px' }}>
                    {t('applicationSent') || 'Application Sent!'}
                  </h3>
                  <p style={{ color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: '1.5' }}>
                    {t('applicationSentDesc') || 'We will contact you shortly about your visa application.'}
                  </p>
                  <button
                    onClick={closeApplyModal}
                    data-testid="visa-apply-done-btn"
                    style={{
                      marginTop: '20px', padding: '10px 28px',
                      background: 'linear-gradient(135deg, #5EFCE8, #00c49a)',
                      border: 'none', borderRadius: '10px', color: '#0a1628',
                      fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                    }}
                  >
                    {t('done') || 'Done'}
                  </button>
                </div>
              ) : (
                <>
                  <h3 style={{ color: isLight ? '#1a1a2e' : '#fff', fontSize: '17px', fontWeight: '700', margin: '0 0 6px' }}>
                    {t('applyForVisa') || 'Apply for Visa'}
                  </h3>
                  <p style={{ color: isLight ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)', fontSize: '12px', margin: '0 0 24px' }}>
                    {result?.passport} → {result?.destination} • {result?.category}
                  </p>

                  {/* Name */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('fullName') || 'Full Name'}
                    </label>
                    <input
                      type="text"
                      value={applyForm.name}
                      onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
                      placeholder={t('enterFullName') || 'Enter your full name'}
                      data-testid="visa-apply-name"
                      style={{
                        width: '100%', padding: '11px 14px', fontSize: '14px',
                        background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                        border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px', color: isLight ? '#1a1a2e' : '#fff',
                        outline: 'none', boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('email') || 'Email'}
                    </label>
                    <input
                      type="email"
                      value={applyForm.email}
                      onChange={(e) => setApplyForm({ ...applyForm, email: e.target.value })}
                      placeholder={t('enterEmail') || 'Enter your email address'}
                      data-testid="visa-apply-email"
                      style={{
                        width: '100%', padding: '11px 14px', fontSize: '14px',
                        background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                        border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px', color: isLight ? '#1a1a2e' : '#fff',
                        outline: 'none', boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Mobile */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('mobileNumber') || 'Mobile Number'}
                    </label>
                    <input
                      type="tel"
                      value={applyForm.mobile}
                      onChange={(e) => setApplyForm({ ...applyForm, mobile: e.target.value })}
                      placeholder={t('enterMobile') || 'Enter your mobile number'}
                      data-testid="visa-apply-mobile"
                      style={{
                        width: '100%', padding: '11px 14px', fontSize: '14px',
                        background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                        border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px', color: isLight ? '#1a1a2e' : '#fff',
                        outline: 'none', boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* UAE Resident */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('areYouUAEResident') || 'Are you a resident of UAE?'}
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {['yes', 'no'].map((val) => (
                        <button
                          key={val}
                          onClick={() => setApplyForm({ ...applyForm, uaeResident: val })}
                          data-testid={`visa-apply-uae-${val}`}
                          style={{
                            flex: 1,
                            padding: '11px',
                            borderRadius: '10px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: applyForm.uaeResident === val
                              ? '2px solid #5EFCE8'
                              : isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                            background: applyForm.uaeResident === val
                              ? 'rgba(94, 252, 232, 0.12)'
                              : isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                            color: applyForm.uaeResident === val
                              ? '#5EFCE8'
                              : isLight ? '#666' : '#999'
                          }}
                        >
                          {val === 'yes' ? (t('yes') || 'Yes') : (t('no') || 'No')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleApplySubmit}
                    disabled={!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident || applySubmitting}
                    data-testid="visa-apply-submit"
                    style={{
                      width: '100%',
                      padding: '13px',
                      background: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                        ? 'rgba(0,212,170,0.15)'
                        : 'linear-gradient(135deg, #5EFCE8 0%, #00c49a 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                        ? (isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.35)')
                        : '#0a1628',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                        ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                        ? 'none' : '0 4px 14px rgba(94, 252, 232, 0.3)'
                    }}
                  >
                    {applySubmitting ? (
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Send size={16} />
                    )}
                    {applySubmitting ? (t('submitting') || 'Submitting...') : (t('submitApplication') || 'Submit Application')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <section id="visa-checker" className="content-section" data-testid="visa-checker-section">
      <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '8px' }}>
        {t('visaChecker') || 'Visa Checker'}
      </h2>
      <p style={{ textAlign: 'center', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '28px' }}>
        {t('visaCheckerDesc') || 'Check visa requirements for any destination worldwide'}
      </p>

      <div style={{
        maxWidth: '600px', margin: '0 auto', padding: '24px',
        background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)', borderRadius: '16px',
        border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.08)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Passport Country */}
          <div>
            <label style={{ color: isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Globe size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
              {t('yourCitizenship') || 'Your Citizenship'}
            </label>
            <FlagSelect
              value={passport}
              onChange={(val) => { setPassport(val); setResult(null); }}
              placeholder={t('selectCountry') || 'Select country...'}
              countries={COUNTRIES}
              testId="visa-checker-passport-full"
              isLight={isLight}
            />
          </div>

          {/* Destination */}
          <div>
            <label style={{ color: isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Plane size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
              {t('destination') || 'Destination'}
            </label>
            <FlagSelect
              value={destination}
              onChange={(val) => { setDestination(val); setResult(null); }}
              placeholder={t('selectCountry') || 'Select country...'}
              countries={COUNTRIES}
              testId="visa-checker-destination-full"
              isLight={isLight}
            />
          </div>

          {/* Check Button */}
          <button
            onClick={handleCheck}
            disabled={!passport || !destination || loading}
            data-testid="visa-checker-btn"
            style={{
              padding: '14px', background: (!passport || !destination) ? 'rgba(0,212,170,0.2)' : '#5EFCE8',
              border: 'none', borderRadius: '10px', color: (!passport || !destination) ? (isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.4)') : '#0a1628',
              fontSize: '15px', fontWeight: '700', cursor: (!passport || !destination) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.2s ease', marginTop: '4px'
            }}
          >
            {loading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={18} />}
            {loading ? (t('checking') || 'Checking...') : (t('checkVisa') || 'Check Visa Requirements')}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ marginTop: '16px', padding: '12px 16px', background: 'rgba(255, 77, 106, 0.1)', border: '1px solid rgba(255, 77, 106, 0.3)', borderRadius: '10px', color: '#FF4D6A', fontSize: '14px', textAlign: 'center' }} data-testid="visa-checker-error">
            {error}
          </div>
        )}

        {/* Result */}
        {result && cfg && (
          <div
            data-testid="visa-checker-result"
            style={{
              marginTop: '20px', padding: '20px', background: cfg.bg,
              border: `1px solid ${cfg.border}`, borderRadius: '14px',
              textAlign: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '6px' }}>
              <ResultIcon size={28} style={{ color: cfg.color }} />
              <span style={{ fontSize: '22px', fontWeight: '700', color: cfg.color }} data-testid="visa-result-status">
                {t(cfg.labelKey) || cfg.labelKey}
              </span>
            </div>

            {/* Description */}
            <p style={{ 
              color: isLight ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.6)', 
              fontSize: '13px', 
              marginBottom: '12px',
              lineHeight: '1.5'
            }}>
              {t(cfg.descKey) || cfg.descKey}
            </p>
            
            <p style={{ color: isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '4px' }}>
              {result.passport} <span style={{ margin: '0 8px', color: cfg.color }}>&rarr;</span> {result.destination}
            </p>
            {result.duration && (
              <p style={{ color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                {t('stayDuration') || 'Stay duration'}: <span style={{ color: cfg.color, fontWeight: '600' }}>{result.duration} {t('days') || 'days'}</span>
              </p>
            )}

            {/* Apply Now button for eVisa/VOA/ETA */}
            {canApply && (
              <button
                onClick={() => setShowApplyModal(true)}
                data-testid="visa-apply-btn-full"
                style={{
                  marginTop: '16px',
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #5EFCE8 0%, #00c49a 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#0a1628',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 14px rgba(94, 252, 232, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                <Send size={16} />
                {t('applyNow') || 'Apply Now'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Apply Modal (full view) */}
      {showApplyModal && (
        <div
          data-testid="visa-apply-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeApplyModal(); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '20px'
          }}
        >
          <div
            data-testid="visa-apply-modal"
            style={{
              background: isLight ? '#fff' : '#1a2235',
              borderRadius: '20px',
              padding: '28px 24px',
              width: '100%',
              maxWidth: '420px',
              border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative'
            }}
          >
            <button
              onClick={closeApplyModal}
              data-testid="visa-apply-modal-close"
              style={{
                position: 'absolute', top: '14px', right: '14px',
                background: 'none', border: 'none', cursor: 'pointer',
                color: isLight ? '#666' : '#888', padding: '4px'
              }}
            >
              <X size={20} />
            </button>

            {applySuccess ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '50%',
                  background: 'rgba(94, 252, 232, 0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <Check size={28} style={{ color: '#5EFCE8' }} />
                </div>
                <h3 style={{ color: isLight ? '#1a1a2e' : '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 8px' }}>
                  {t('applicationSent') || 'Application Sent!'}
                </h3>
                <p style={{ color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: '1.5' }}>
                  {t('applicationSentDesc') || 'We will contact you shortly about your visa application.'}
                </p>
                <button
                  onClick={closeApplyModal}
                  style={{
                    marginTop: '20px', padding: '10px 28px',
                    background: 'linear-gradient(135deg, #5EFCE8, #00c49a)',
                    border: 'none', borderRadius: '10px', color: '#0a1628',
                    fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                  }}
                >
                  {t('done') || 'Done'}
                </button>
              </div>
            ) : (
              <>
                <h3 style={{ color: isLight ? '#1a1a2e' : '#fff', fontSize: '17px', fontWeight: '700', margin: '0 0 6px' }}>
                  {t('applyForVisa') || 'Apply for Visa'}
                </h3>
                <p style={{ color: isLight ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)', fontSize: '12px', margin: '0 0 24px' }}>
                  {result?.passport} → {result?.destination} • {result?.category}
                </p>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('fullName') || 'Full Name'}
                  </label>
                  <input
                    type="text"
                    value={applyForm.name}
                    onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
                    placeholder={t('enterFullName') || 'Enter your full name'}
                    data-testid="visa-apply-name-full"
                    style={{
                      width: '100%', padding: '11px 14px', fontSize: '14px',
                      background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                      border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px', color: isLight ? '#1a1a2e' : '#fff',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('email') || 'Email'}
                  </label>
                  <input
                    type="email"
                    value={applyForm.email}
                    onChange={(e) => setApplyForm({ ...applyForm, email: e.target.value })}
                    placeholder={t('enterEmail') || 'Enter your email address'}
                    data-testid="visa-apply-email-full"
                    style={{
                      width: '100%', padding: '11px 14px', fontSize: '14px',
                      background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                      border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px', color: isLight ? '#1a1a2e' : '#fff',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('mobileNumber') || 'Mobile Number'}
                  </label>
                  <input
                    type="tel"
                    value={applyForm.mobile}
                    onChange={(e) => setApplyForm({ ...applyForm, mobile: e.target.value })}
                    placeholder={t('enterMobile') || 'Enter your mobile number'}
                    data-testid="visa-apply-mobile-full"
                    style={{
                      width: '100%', padding: '11px 14px', fontSize: '14px',
                      background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                      border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px', color: isLight ? '#1a1a2e' : '#fff',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('areYouUAEResident') || 'Are you a resident of UAE?'}
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {['yes', 'no'].map((val) => (
                      <button
                        key={val}
                        onClick={() => setApplyForm({ ...applyForm, uaeResident: val })}
                        data-testid={`visa-apply-uae-${val}-full`}
                        style={{
                          flex: 1, padding: '11px', borderRadius: '10px',
                          fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          border: applyForm.uaeResident === val
                            ? '2px solid #5EFCE8'
                            : isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                          background: applyForm.uaeResident === val
                            ? 'rgba(94, 252, 232, 0.12)'
                            : isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                          color: applyForm.uaeResident === val
                            ? '#5EFCE8' : isLight ? '#666' : '#999'
                        }}
                      >
                        {val === 'yes' ? (t('yes') || 'Yes') : (t('no') || 'No')}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleApplySubmit}
                  disabled={!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident || applySubmitting}
                  data-testid="visa-apply-submit-full"
                  style={{
                    width: '100%', padding: '13px',
                    background: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                      ? 'rgba(0,212,170,0.15)'
                      : 'linear-gradient(135deg, #5EFCE8 0%, #00c49a 100%)',
                    border: 'none', borderRadius: '10px',
                    color: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                      ? (isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.35)') : '#0a1628',
                    fontSize: '14px', fontWeight: '700',
                    cursor: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                      ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    boxShadow: (!applyForm.name || !applyForm.email || !applyForm.mobile || !applyForm.uaeResident)
                      ? 'none' : '0 4px 14px rgba(94, 252, 232, 0.3)'
                  }}
                >
                  {applySubmitting ? (
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Send size={16} />
                  )}
                  {applySubmitting ? (t('submitting') || 'Submitting...') : (t('submitApplication') || 'Submit Application')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default VisaChecker;
